import { createGuidesText } from "../../../shared/utils/translations/guides2Texts";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Header } from "antd/es/layout/layout";
import { Alert, Button, Skeleton } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { UiTextsContext, LangContext } from "../../contexts/Contexts";
import ResponsiveNavigation from "../../components/NewSideBar/ResponsiveNavigation";
import NewNavBar from "../newHome/NewNavBar/NewNavBar";
import { getGuideContentService } from "../../../infrastructure/services/Guides2Service";
import { GUIDE_VARIANTS, isGuideVariant } from "../../../shared/constants/guideVariants";
import { resolveGuide2ViewerBackPath } from "../../routes/guides2Origins";
import CommentsSection from "../../components/comments";
import "./Styles/Guides2Viewer.css";

// Layout dedicado para ver una guía de Guides 2.0 (ruta propia /guides2/:guideId),
// separado de Guides2.jsx (que solo lanza hacia acá) para que la guía se vea en
// una página nueva y no apilada debajo del launcher. Mismo criterio de
// aislamiento que Guides2.jsx: <iframe sandbox>, nunca dangerouslySetInnerHTML
// con el HTML completo, nunca se referencia result.data.fileUrl.
const Guides2Viewer = () => {
  const { texts } = useContext(UiTextsContext);
  const { lang } = useContext(LangContext);
  const t = createGuidesText(lang === "en");
  const navigate = useNavigate();
  const { guideId, variantTypeCode } = useParams();
  // En HashRouter el query vive DENTRO del hash, asi que solo lo ve el router:
  // `window.location.search` vendria vacio.
  const location = useLocation();

  const [guide, setGuide] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Falta de acceso a la variante: no es un error, es una respuesta legítima
  // del backend (200 con hasAccess = false) y se muestra con otro tono.
  const [accessMessage, setAccessMessage] = useState("");
  const blobUrlRef = useRef(null);

  // VIVA-1166: el hilo de feedback es de quien COMPRO la guia, y leer la version
  // gratuita no es comprarla. La variante de la ruta es lo que distingue las dos
  // lecturas —`/guides2/:guideId/FREE` y `/guides2/:guideId/PAID`—, asi que el
  // hilo solo cuelga de la de pago.
  const esVariantePagada = variantTypeCode === GUIDE_VARIANTS.PAID;

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => {
    let active = true;

    const loadGuide = async () => {
      setLoading(true);
      setError("");
      setAccessMessage("");

      try {
        if (!isGuideVariant(variantTypeCode)) {
          setError(t("viewerUnknownVariant"));
          return;
        }

        const result = await getGuideContentService({
          guideId: Number(guideId),
          variantTypeCode,
        });

        if (!active) return;

        // Desde VIVA-312 el backend no lanza error cuando el usuario no compró
        // la variante de pago: responde 200 con hasAccess en false y el resto
        // de los campos en null. Hay que revisarlo ANTES de tocar el contenido.
        if (result?.data?.hasAccess === false) {
          setAccessMessage(result?.message || t("viewerNoAccess"));
          return;
        }

        if (!result?.success || !result?.data?.content) {
          setError(result?.message || t("viewerNoContent"));
          return;
        }

        setGuide(result.data);

        revokeBlobUrl();
        const blob = new Blob([result.data.content], { type: "text/html" });
        const nextBlobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = nextBlobUrl;
        if (active) setBlobUrl(nextBlobUrl);
      } catch {
        // Sin console.error: el repository ya dejo esta misma falla en consola,
        // con el nombre de la operacion. Aqui solo se decide que ve el usuario.
        if (active) {
          setError(t("viewerLoadError"));
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadGuide();

    return () => {
      active = false;
      revokeBlobUrl();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideId, variantTypeCode]);

  return (
    <>
      <Header className="guides2-viewer-page-header">
        <NewNavBar />
      </Header>

      <ResponsiveNavigation
        uiTexts={texts}
        className="guides2-viewer-sidebar-navigation"
      />

      <div className="container-guides2-viewer">
        <div className="g2v-topbar">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(resolveGuide2ViewerBackPath(location.search))}
          >
            {t("viewerBack")}
          </Button>
          {guide ? <h1 className="g2v-title">{guide.name}</h1> : null}
          {/* Aqui NO va el distintivo Beta. Llego a estar, como "superficie 3"
              de VIVA-1246, y se retiro el 22/09/2026: el distintivo es de la
              tarjeta del catalogo, no de la guia ya abierta. Por eso
              `obtener/contenido-guia` no devuelve `isBeta` —no es un hueco del
              contrato— y volver a ponerlo aqui obligaria a pedir
              `obtener/detalle-guia` en cada lectura solo para pintar una
              etiqueta. Hay una prueba que lo deja fijado en cero. */}
        </div>

        {loading ? (
          <div className="g2v-loading">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : accessMessage ? (
          <Alert type="warning" message={accessMessage} showIcon />
        ) : error ? (
          <Alert type="error" message={error} showIcon />
        ) : (
          <>
            <iframe
              key={blobUrl}
              src={blobUrl}
              title={guide?.name || "Guide"}
              className="g2v-iframe"
              // allow-same-origin: el HTML de la guía usa localStorage propio
              // para su estado (diagnóstico/quiz); sin esto, sus scripts
              // truenan al primer acceso y los botones quedan sin efecto.
              // Sigue sin poder navegar el top-level ni salir del sandbox.
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />

            {/* VIVA-1166 · hilo de comentarios de la guía, debajo del contenido.
                Es la sección reutilizable de `components/comments`, la misma que
                nació en el detalle del evento; aquí solo se le pasan los textos
                propios de Guías.

                QUIÉN LO VE, que son dos condiciones y no una:

                  1. Que la guía se haya podido leer. Esta rama es justo la que se
                     pinta cuando `obtener/contenido-guia` entregó el contenido: si
                     el backend responde `hasAccess: false` o la guía no carga,
                     arriba se muestra el aviso y aquí no se pinta nada. No hace
                     falta una comprobación aparte, y ponerla sería una segunda
                     fuente de verdad sobre el acceso.
                  2. Que sea la VERSIÓN DE PAGO (`esVariantePagada`). La historia
                     restringe el hilo a quien compró la guía, y entrar por
                     `/FREE` no es haber comprado: esa versión la abre cualquiera
                     desde el catálogo. En una guía gratuita —sin variante de pago—
                     no hay comprador, así que tampoco hay hilo.

                ESCONDER LA SECCIÓN NO ES CONTROL DE ACCESO, y conviene que quien
                lea esto no confunda las dos cosas. Lo de arriba hace que la
                pantalla sea COHERENTE con quién debe ver el hilo; la barrera de
                verdad tiene que estar en el servidor: quien no compró la guía debe
                recibir un rechazo explícito al pedir el hilo y al intentar
                publicar, porque el endpoint se puede llamar directamente sin pasar
                por esta pantalla. Mientras esos endpoints no existan, aquí no hay
                nada que proteger; el día que existan, `hasAccess` de la interfaz
                sigue sin ser la garantía.

                Los roles de administración tampoco se resuelven aquí: la historia
                les da visibilidad del hilo, pero las pantallas de administración
                viven en AdminFront, que es otro repositorio.

                SIN BACKEND TODAVÍA: en `Guides2Repository` no hay ningún endpoint
                de comentarios —solo catálogo, detalle, compradas, contenido y
                pago—, así que la sección va SIN la prop `acciones` y lo que se
                escribe vive en el estado del componente hasta que se recargue.
                Está a la vista para revisar el diseño y poder demostrarlo; el día
                que existan los endpoints, conectar es pasar `acciones` (el
                contrato está en `components/comments/README.md`) y nada más de
                esta pantalla cambia.

                LO QUE ESTA PANTALLA NO PUEDE CUMPLIR DE LA HISTORIA, y no es
                olvido: asociar el comentario a la lámina desde la que se escribió
                pide saber en cuál va el lector, y la guía es un HTML opaco dentro
                de un <iframe sandbox> —haría falta el puente de `postMessage`
                descrito en `CLAUDE.md` §7. La respuesta oficial a nombre de Víctor
                Nómada depende de VIVA-1217, que es la cuenta misma. */}
            {esVariantePagada && (
              <CommentsSection
                className="g2v-feedback"
                recursoId={Number(guideId)}
                /* Comentar, no reaccionar: la historia pide un hilo de feedback
                   y el catálogo de emojis es del detalle del evento. */
                mostrarReacciones={false}
                textos={{
                  title: t("feedbackTitle"),
                  placeholder: t("feedbackPlaceholder"),
                  emptyTitle: t("feedbackEmptyTitle"),
                  emptyText: t("feedbackEmptyText"),
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Guides2Viewer;
